import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Avatar,
  Tabs,
  Tab,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';

const Users = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, pendingRes] = await Promise.all([
        axios.get('/api/users'),
        axios.get('/api/users/pending'),
      ]);
      setUsers(usersRes.data.filter(u => u.status === 'approved'));
      setPendingUsers(pendingRes.data);
    } catch (error) {
      console.error('Ошибка загрузки пользователей:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.patch(`/api/users/${id}/approve`);
      fetchData();
    } catch (error) {
      console.error('Ошибка одобрения:', error);
    }
  };

  const handleReject = async (id) => {
    if (window.confirm('Вы уверены, что хотите отклонить этого пользователя?')) {
      try {
        await axios.patch(`/api/users/${id}/reject`);
        fetchData();
      } catch (error) {
        console.error('Ошибка отклонения:', error);
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить этого пользователя?')) {
      try {
        await axios.delete(`/api/users/${id}`);
        fetchData();
      } catch (error) {
        console.error('Ошибка удаления:', error);
      }
    }
  };

  const UserCard = ({ user, isPending = false, index = 0 }) => (
    <Card
      sx={{
        background: isDarkMode ? 'rgba(22, 27, 34, 0.6)' : '#ffffff',
        backdropFilter: isDarkMode ? 'blur(20px)' : 'none',
        border: '1px solid',
        borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s ease',
        animation: `fadeInUp 0.4s ease ${index * 0.05}s both`,
        '@keyframes fadeInUp': {
          '0%': { opacity: 0, transform: 'translateY(20px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: isDarkMode 
            ? '0 12px 40px rgba(0, 0, 0, 0.4)'
            : '0 12px 40px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              mr: 2,
              fontWeight: 700,
              background: user.role === 'admin' 
                ? 'linear-gradient(135deg, #b48ead 0%, #bf616a 100%)'
                : 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)',
              boxShadow: isDarkMode 
                ? '0 4px 12px rgba(0, 0, 0, 0.3)'
                : '0 4px 12px rgba(0, 0, 0, 0.1)',
            }}
          >
            {user.firstName?.[0]}{user.lastName?.[0]}
          </Avatar>
          <Box flex={1}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {user.firstName} {user.lastName}
            </Typography>
            <Chip
              label={user.role === 'admin' ? 'Администратор' : 'Менеджер'}
              size="small"
              sx={{
                mt: 0.5,
                background: user.role === 'admin'
                  ? 'rgba(180, 142, 173, 0.2)'
                  : 'rgba(136, 192, 208, 0.2)',
                color: user.role === 'admin' ? '#b48ead' : '#88c0d0',
                border: '1px solid',
                borderColor: user.role === 'admin' 
                  ? 'rgba(180, 142, 173, 0.3)' 
                  : 'rgba(136, 192, 208, 0.3)',
                fontWeight: 600,
              }}
            />
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
            📧 {user.email}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
            📱 {user.phone}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            📅 {new Date(user.createdAt).toLocaleDateString('ru-RU')}
          </Typography>
        </Box>

        {isPending ? (
          <Box mt={2} display="flex" gap={1}>
            <Button
              variant="contained"
              startIcon={<ApproveIcon />}
              onClick={() => handleApprove(user._id)}
              fullWidth
              sx={{
                background: 'linear-gradient(135deg, #a3be8c 0%, #88c0d0 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #88c0d0 0%, #a3be8c 100%)',
                }
              }}
            >
              Одобрить
            </Button>
            <Button
              variant="contained"
              startIcon={<RejectIcon />}
              onClick={() => handleReject(user._id)}
              fullWidth
              sx={{
                background: 'linear-gradient(135deg, #bf616a 0%, #d08770 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #d08770 0%, #bf616a 100%)',
                }
              }}
            >
              Отклонить
            </Button>
          </Box>
        ) : (
          user.role !== 'admin' && (
            <Box mt={2}>
              <IconButton
                onClick={() => handleDelete(user._id)}
                size="small"
                sx={{ color: '#bf616a' }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          )
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Layout>
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="60vh"
          flexDirection="column"
          gap={2}
        >
          <CircularProgress size={60} thickness={2} />
          <Typography variant="body2" color="text.secondary">
            Загрузка пользователей...
          </Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 800,
            background: isDarkMode 
              ? 'linear-gradient(135deg, #e6edf3 0%, #88c0d0 100%)'
              : 'linear-gradient(135deg, #2e3440 0%, #5e81ac 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          👥 Управление пользователями
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Одобряйте новых менеджеров и управляйте пользователями системы
        </Typography>
      </Box>

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              background: 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)',
              border: 'none',
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center">
                <PeopleIcon sx={{ fontSize: 50, mr: 2, color: '#0d1117' }} />
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 800, color: '#0d1117' }}>
                    {users.length}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(0,0,0,0.7)' }}>
                    Активных пользователей
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              background: 'linear-gradient(135deg, #ebcb8b 0%, #d08770 100%)',
              border: 'none',
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center">
                <PendingIcon sx={{ fontSize: 50, mr: 2, color: '#0d1117' }} />
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 800, color: '#0d1117' }}>
                    {pendingUsers.length}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(0,0,0,0.7)' }}>
                    Ожидают одобрения
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Табы */}
      <Box 
        sx={{ 
          borderBottom: 1, 
          borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)', 
          mb: 3 
        }}
      >
        <Tabs 
          value={tabValue} 
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{
            '& .MuiTabs-indicator': {
              background: 'linear-gradient(90deg, #88c0d0, #b48ead)',
              height: 3,
              borderRadius: 2,
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              color: 'text.secondary',
              '&.Mui-selected': {
                color: isDarkMode ? '#88c0d0' : '#5e81ac',
              }
            }
          }}
        >
          <Tab label={`Активные пользователи (${users.length})`} />
          <Tab label={`Ожидают одобрения (${pendingUsers.length})`} />
        </Tabs>
      </Box>

      {/* Список пользователей */}
      <Grid container spacing={3}>
        {tabValue === 0 ? (
          users.length > 0 ? (
            users.map((user, index) => (
              <Grid item xs={12} md={6} lg={4} key={user._id}>
                <UserCard user={user} index={index} />
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Box textAlign="center" py={8}>
                <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                  Нет активных пользователей
                </Typography>
              </Box>
            </Grid>
          )
        ) : (
          pendingUsers.length > 0 ? (
            pendingUsers.map((user, index) => (
              <Grid item xs={12} md={6} lg={4} key={user._id}>
                <UserCard user={user} isPending index={index} />
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Box textAlign="center" py={8}>
                <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                  Нет пользователей, ожидающих одобрения
                </Typography>
              </Box>
            </Grid>
          )
        )}
      </Grid>
    </Layout>
  );
};

export default Users;
